/**
 * Root application component.
 *
 * Provider order (§11.2 of CC_LLD_PRD_0007.md):
 *   ThemeProvider → MsalProvider → QueryClientProvider → MotionProvider → RouterProvider
 *
 * websocketManager.initialize() is moved OUT of this component and into
 * AppLayout (gated on useIsAuthenticated), so the socket only connects
 * post-auth and the auth callback always has a valid token.
 */

import { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MsalProvider } from '@azure/msal-react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { ApplicationToaster } from './components/common/ApplicationToaster'
import { ThemeProvider } from './contexts/ThemeContext'
import { MotionProvider } from './contexts/MotionContext'
import {
  HINT_DELAY_MS,
  HINT_SKIP_DELAY_MS,
} from './components/ui/console/ConsoleHint'
import { router } from './routes'
import { setQueryClientForTokenHandler } from './services/tokenEventHandler'
import { msalInstance } from './services/msal'
import { reportRouterError } from './utils/rootErrorReporting'

// Create QueryClient instance with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
})

function App() {
  // Registers the query client for token-driven cache invalidation; the
  // socket itself is owned entirely by AppLayout's auth-gated effect.
  useEffect(() => {
    setQueryClientForTokenHandler(queryClient)
  }, [])

  return (
    <ThemeProvider>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <MotionProvider>
            {/* One provider for every hint and info tooltip in the application.
              The "recently open" state skipDelayDuration drives lives per
              provider, so a rail of hints under this one provider opens its
              second hint without the delay; a provider per instance would pay
              the delay on every one. */}
            <TooltipPrimitive.Provider
              delayDuration={HINT_DELAY_MS}
              skipDelayDuration={HINT_SKIP_DELAY_MS}
            >
              <RouterProvider router={router} onError={reportRouterError} />
              {/* Global toast surface — used by PRD mutation error handling
                and available to any future surface. react-hot-toast takes
                inline styles rather than classes, so the semantic roles arrive
                here by import: a toast is the same panel as every other
                surface, with its border carrying the outcome. */}
              <ApplicationToaster />
            </TooltipPrimitive.Provider>
          </MotionProvider>
        </QueryClientProvider>
      </MsalProvider>
    </ThemeProvider>
  )
}

export default App
